import React, { useContext, useEffect, useState, useCallback } from 'react';
import config from "../../config";
import useHttp from '../../hooks/useHttp';
import { useLocation } from "react-router-dom";
import logo from "../../images/logo-masorden-blanco-small.svg";
import { Hidden, Drawer, List, ListItem, ListItemText } from "@gti-controls/web";
import homeStyles from "../../common/styles/Home";
import { AuthContext } from "../../providers/auth";
import { SettingsContext } from '../../providers/settings';

/**
 * Normaliza la respuesta de la API de menú items.
 * El lambda puede devolver un objeto si es un solo resultado, un array, o null/undefined.
 */
const normalizeMenuResponse = (response) => {
	if (!response) return [];
	if (Array.isArray(response)) return response;
	if (typeof response === 'object') return [response];
	return [];
};

/**
 * Construye un árbol jerárquico a partir de la lista plana de items.
 * Compatible con respuestas sin campos de jerarquía (producción actual).
 */
const buildMenuTree = (flatItems) => {
	if (!flatItems || flatItems.length === 0) return [];

	const itemMap = {};
	for (const item of flatItems) {
		itemMap[item.ItemID] = {
			...item,
			MenuItemPadre: item.MenuItemPadre ?? 0,
			MenuItemNivel: item.MenuItemNivel ?? 0,
			MenuItemOrden: item.MenuItemOrden ?? 0,
			children: [],
		};
	}

	const rootItems = [];
	for (const item of flatItems) {
		const node = itemMap[item.ItemID];
		const parentId = node.MenuItemPadre;
		if (!parentId || parentId === 0) {
			rootItems.push(node);
		} else if (itemMap[parentId]) {
			itemMap[parentId].children.push(node);
		} else {
			rootItems.push(node);
		}
	}

	rootItems.sort((a, b) => a.MenuItemOrden - b.MenuItemOrden);
	for (const node of Object.values(itemMap)) {
		if (node.children.length > 0) {
			node.children.sort((a, b) => a.MenuItemOrden - b.MenuItemOrden);
		}
		// Un nodo es agrupador si tiene hijos (no por VerboClassName vacío)
		node.isGrouper = node.children.length > 0;
	}

	return rootItems;
};

/**
 * Encuentra el primer ítem navegable (no agrupador) en el árbol.
 */
const findFirstNavigableItem = (tree) => {
	for (const node of tree) {
		if (!node.isGrouper) return node;
		if (node.children.length > 0) {
			const child = findFirstNavigableItem(node.children);
			if (child) return child;
		}
	}
	return null;
};

/**
 * Determina el estado inicial de expansión del menú.
 */
const getInitialExpansionState = (tree) => {
	const expandedIds = new Set();
	let autoNavigateItem = null;

	if (tree.length === 0) return { expandedIds, autoNavigateItem };

	const parentNodes = tree.filter(node => node.isGrouper && node.children.length > 0);

	if (parentNodes.length === 1 && tree.length === 1) {
		expandedIds.add(parentNodes[0].ItemID);
		autoNavigateItem = parentNodes[0].children[0] || null;
	} else if (tree.length > 0) {
		autoNavigateItem = findFirstNavigableItem(tree);
	}

	return { expandedIds, autoNavigateItem };
};

/**
 * Aplana el árbol para obtener todos los items navegables (para handleMenuItemsChange).
 */
const flattenNavigableItems = (tree) => {
	const items = [];
	for (const node of tree) {
		if (!node.isGrouper) items.push(node);
		if (node.children.length > 0) {
			items.push(...flattenNavigableItems(node.children));
		}
	}
	return items;
};

const Sidebar = () => {
	const location = useLocation();
	const classes = homeStyles();
	const { state: authContext } = useContext(AuthContext);
	const {
		settings,
		isSelectingAccount,
		actionSelected,
		handleMenuItemsChange,
		handleDrawerToggle,
		handleOptionChange,
		handleActionSelected,
	} = React.useContext(SettingsContext);
	const { sendRequest: fetchMenuItems } = useHttp();
	const [menuTree, setMenuTree] = useState([]);
	const [expandedIds, setExpandedIds] = useState(new Set());

	const toggleExpand = useCallback((itemId) => {
		setExpandedIds(prev => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	}, []);

	const handleItemClick = useCallback((node) => {
		if (node.isGrouper) {
			if (node.children.length > 0) {
				toggleExpand(node.ItemID);
			}
		} else {
			handleOptionChange(node, settings.mobileDrawerOpened, null);
		}
	}, [toggleExpand, handleOptionChange, settings.mobileDrawerOpened]);

	useEffect(() => {
		const pathArray = location.pathname.split("/");
		const keep = (pathArray.length > 2 && location?.state) || (pathArray.length > 2 && location?.search);
		const action = (pathArray[2] || null) && location?.state?.action ? location.state.action : null;
		handleActionSelected(action);

		const processMenuItems = (rawData) => {
			const normalized = normalizeMenuResponse(rawData);
			if (normalized.length === 0) return;

			const tree = buildMenuTree(normalized);
			setMenuTree(tree);

			const navigableItems = flattenNavigableItems(tree);
			if (navigableItems.length === 0) return;

			handleMenuItemsChange(navigableItems);

			const { expandedIds: initialExpanded, autoNavigateItem } = getInitialExpansionState(tree);
			setExpandedIds(initialExpanded);

			const urlOption = navigableItems.find(
				option => option.VerboClassName.replace('#', '') === pathArray[1] || null
			);
			const firstNavigable = autoNavigateItem || navigableItems[0];
			handleOptionChange(
				urlOption ? urlOption : firstNavigable,
				false,
				firstNavigable,
				keep,
				action
			);
		};

		if (authContext.user.sesionId) {
			fetchMenuItems({
				url: config.API_URL + `rights/menu/items/` + authContext.user.sesionId
			}, processMenuItems);
		}

	// eslint-disable-next-line
	}, [authContext]);

	const renderMenuItem = (node) => {
		if (node.isGrouper) {
			const isExpanded = expandedIds.has(node.ItemID);
			const hasChildren = node.children.length > 0;
			return (
				<React.Fragment key={node.ItemID}>
					<ListItem
						button
						className={classes.menuItems}
						onClick={() => handleItemClick(node)}
						style={{ cursor: hasChildren ? 'pointer' : 'default' }}
					>
						<ListItemText primary={node.Texto} />
						{hasChildren && (
							<span style={{ color: '#ffffff', fontSize: '12px', marginLeft: '8px' }}>
								{isExpanded ? '▲' : '▼'}
							</span>
						)}
					</ListItem>
					{hasChildren && isExpanded && (
						<List disablePadding>
							{node.children.map(child => (
								<ListItem
									button
									className={classes.menuItems}
									selected={settings.menuOptionSelected.ItemID === child.ItemID}
									key={child.ItemID}
									onClick={() => handleItemClick(child)}
									style={{ paddingLeft: '40px' }}
								>
									<ListItemText primary={child.Texto} />
								</ListItem>
							))}
						</List>
					)}
				</React.Fragment>
			);
		}

		return (
			<ListItem
				button
				className={classes.menuItems}
				selected={settings.menuOptionSelected.ItemID === node.ItemID}
				key={node.ItemID}
				onClick={() => handleItemClick(node)}
			>
				<ListItemText primary={node.Texto} />
			</ListItem>
		);
	};

	const drawerList = (
		<List>
			{menuTree.map(node => renderMenuItem(node))}
		</List>
	);

	return (
		<div>
			{(!isSelectingAccount || authContext.accountsLength === 1) && (!actionSelected) ? (
				<nav className={classes.drawer}>
					<Hidden smUp implementation="css">
						<Drawer
							variant="temporary"
							anchor={'left'}
							open={settings.mobileDrawerOpened}
							onClose={() => handleDrawerToggle()}
							classes={{
								paper: classes.drawerPaper,
							}}
							ModalProps={{
								keepMounted: true,
							}}
						>
							{drawerList}
						</Drawer>
					</Hidden>
					<Hidden xsDown implementation="css">
						<Drawer
							classes={{
								paper: classes.drawerPaper,
							}}
							variant="permanent"
							open
						>
							<div className={classes.toolbar}>
								<div className={classes.drawerLogo}>
									<img src={logo} alt="Logo" className={classes.drawerImageIcon} />
								</div>
							</div>
							{drawerList}
						</Drawer>
					</Hidden>
				</nav>
			) : null}
		</div>
	);
};

export default Sidebar;
